import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Package, X, Trash2, Rocket, Pencil } from "lucide-react";

const EMPTY_ITEM = { title: "", item_type: "task", priority: "medium", relative_due_day_offset: "", estimated_hours: "" };
const EMPTY_FORM = { name: "", description: "", items: [{ ...EMPTY_ITEM }] };

const PRIORITY_STYLES = {
  low: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
  medium: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  high: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  critical: "text-rose-400 bg-rose-500/10 border-rose-500/20",
};

export default function TaskBundles() {
  const [bundles, setBundles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);
  const [applyForm, setApplyForm] = useState({ project_id: "", start_date: new Date().toISOString().slice(0, 10), assignee: "" });
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try {
      const [bRes, pRes] = await Promise.all([
        api.get("/task-bundles"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setBundles(bRes.data || []);
      setProjects(pRes.data || []);
    } catch {
      toast.error("Failed to load task bundles");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (b) => {
    setEditingId(b.bundle_id);
    setForm({
      name: b.name,
      description: b.description || "",
      items: b.items.length ? b.items.map(i => ({
        title: i.title, item_type: i.item_type || "task", priority: i.priority || "medium",
        relative_due_day_offset: i.relative_due_day_offset ?? "", estimated_hours: i.estimated_hours ?? "",
      })) : [{ ...EMPTY_ITEM }],
    });
    setShowModal(true);
  };

  const updateItem = (idx, patch) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Bundle name is required");
    const items = form.items
      .filter(i => i.title.trim())
      .map(i => ({
        title: i.title.trim(), item_type: i.item_type, priority: i.priority,
        relative_due_day_offset: i.relative_due_day_offset === "" ? null : parseInt(i.relative_due_day_offset, 10),
        estimated_hours: i.estimated_hours === "" ? null : parseFloat(i.estimated_hours),
      }));
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/task-bundles/${editingId}`, { name: form.name, description: form.description, items });
        toast.success("Bundle updated");
      } else {
        await api.post("/task-bundles", { name: form.name, description: form.description, items });
        toast.success("Bundle created");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save bundle");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"? This does not affect tasks already created from it.`)) return;
    try {
      await api.delete(`/task-bundles/${b.bundle_id}`);
      toast.success("Bundle deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete bundle");
    }
  };

  const openApply = (b) => {
    setApplyTarget(b);
    setApplyForm({ project_id: projects[0]?.project_id || "", start_date: new Date().toISOString().slice(0, 10), assignee: "" });
  };

  const apply = async () => {
    if (!applyForm.project_id) return toast.error("Choose a project");
    setApplying(true);
    try {
      const { data } = await api.post(`/task-bundles/${applyTarget.bundle_id}/apply`, {
        project_id: applyForm.project_id,
        start_date: applyForm.start_date || undefined,
        assignee: applyForm.assignee || undefined,
      });
      toast.success(`Added ${data.created_count} task${data.created_count !== 1 ? "s" : ""} to the project`);
      setApplyTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply bundle");
    } finally {
      setApplying(false);
    }
  };

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Task Bundles</h1>
          <p className="text-sm text-[#7d8590] mt-1">Reusable checklists you can drop into any project in one click — e.g. "Client onboarding" or "Security review".</p>
        </div>
        <Button onClick={openCreate} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Bundle
        </Button>
      </div>

      {bundles.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">No bundles yet. Create one to reuse a checklist of tasks across projects.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bundles.map(b => (
            <div key={b.bundle_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-1.5 text-white font-semibold"><Package className="w-3.5 h-3.5 text-[#7d8590]" /> {b.name}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(b)} className="p-1.5 text-[#7d8590] hover:text-white" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(b)} className="p-1.5 text-[#7d8590] hover:text-rose-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {b.description && <p className="text-xs text-[#7d8590] mb-3">{b.description}</p>}
              <div className="text-xs text-[#7d8590] mb-3">{b.items.length} task{b.items.length !== 1 ? "s" : ""}</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {b.items.slice(0, 6).map((it, i) => (
                  <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[it.priority] || PRIORITY_STYLES.medium}`}>{it.title}</span>
                ))}
                {b.items.length > 6 && <span className="text-[10px] text-[#7d8590]">+{b.items.length - 6} more</span>}
              </div>
              <Button onClick={() => openApply(b)} disabled={b.items.length === 0} className="w-full bg-[#161b22] border border-[#30363d] text-white hover:bg-[#1c2128]">
                <Rocket className="w-3.5 h-3.5 mr-1.5" /> Apply to project
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Create/edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">{editingId ? "Edit bundle" : "New bundle"}</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Client onboarding"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-[#7d8590]">Tasks</label>
                  <button onClick={addItem} className="text-xs text-[#00F5D4] hover:underline">+ Add task</button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <input value={it.title} onChange={e => updateItem(idx, { title: e.target.value })}
                        placeholder="Task title"
                        className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2.5 py-1.5 text-xs" />
                      <select value={it.priority} onChange={e => updateItem(idx, { priority: e.target.value })}
                        className="bg-[#161b22] border border-[#30363d] text-white rounded-md px-1.5 py-1.5 text-xs">
                        <option value="low">Low</option>
                        <option value="medium">Med</option>
                        <option value="high">High</option>
                        <option value="critical">Crit</option>
                      </select>
                      <input type="number" value={it.relative_due_day_offset} onChange={e => updateItem(idx, { relative_due_day_offset: e.target.value })}
                        placeholder="Day +" title="Days after apply date"
                        className="w-16 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                      <button onClick={() => removeItem(idx)} className="p-1 text-[#7d8590] hover:text-rose-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={save} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Saving…" : editingId ? "Save changes" : "Create bundle"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Apply modal */}
      {applyTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Apply "{applyTarget.name}"</h2>
              <button onClick={() => setApplyTarget(null)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Project *</label>
                <select value={applyForm.project_id} onChange={e => setApplyForm(f => ({ ...f, project_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">Select project…</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Start date (used for relative due dates)</label>
                <input type="date" value={applyForm.start_date} onChange={e => setApplyForm(f => ({ ...f, start_date: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Assign all tasks to (optional)</label>
                <input value={applyForm.assignee} onChange={e => setApplyForm(f => ({ ...f, assignee: e.target.value }))}
                  placeholder="User ID"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={apply} disabled={applying} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {applying ? "Applying…" : `Create ${applyTarget.items.length} task${applyTarget.items.length !== 1 ? "s" : ""}`}
              </Button>
              <Button onClick={() => setApplyTarget(null)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
